import { sonnerToast } from '../../../../lib/sonner';
import { cn } from '../../../../lib/utils';
import { GhostButton } from '../../buttons';
import { CheckCircleFill, ErrorFill } from '@crabs-ai/web-icons';

export interface ToastOptions {
  /** 토스트 포지션 */
  position?:
    | 'top-left'
    | 'top-right'
    | 'top-center'
    | 'bottom-left'
    | 'bottom-right'
    | 'bottom-center';
  /** 토스트 지속 시간 (ms) */
  duration?: number; // 기본값: 500 (커스텀 가능하도록 설정)
  /** 토스트 닫을 수 있는 옵션 */
  dismissible?: boolean; // 기본값: true (닫을 수 있는 옵션)
  /** 토스트가 닫힐 때 실행되는 콜백 함수 */
  onDismiss?: () => void;
  /** 자동으로 닫힐 때 실행되는 콜백 함수 */
  onAutoClose?: () => void;
  /** 접근성 관련 aria-label */
  containerAriaLabel?: string;
}

export interface ToastBarProps extends Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'id' | 'title' | 'description'
> {
  /** 토스트 고유 식별자 */
  id: string | number;
  /** 토스트 타입 */
  type?: 'success' | 'error';
  /** 토스트 제목 */
  title?: React.ReactNode;
  /** 토스트 설명 */
  description?: React.ReactNode;
  /** 텍스트 오버플로우 처리 여부 */
  isTextOverflow?: boolean;
  /** 액션 버튼 표시 여부 */
  isShowActionButton?: boolean;
  /** 액션 버튼 클릭 핸들러 */
  button?: {
    /** 액션 버튼 라벨 */
    label?: string;
    /** 액션 버튼 클릭 핸들러 */
    onClick: () => void;
  };
  /** 토스트 옵션 */
  toastOptions?: ToastOptions;
}

export default function ToastBar({ ...props }: ToastBarProps) {
  const {
    title,
    description,
    button,
    id,
    isTextOverflow,
    isShowActionButton,
    type,
    className,
    ...rest
  } = props;

  return (
    <div
      className={cn(
        'flex max-w-[312px] min-w-[280px] flex-col rounded-sm bg-comp-toast-bg px-sm py-xs',
        className,
      )}
      {...rest}
    >
      <div className="flex w-full items-center">
        <div className="flex w-full gap-xs">
          {type && (
            <div className="flex">
              {type === 'success' && (
                <CheckCircleFill className="m-5xs size-[24px] [&_path]:fill-comp-toast-icon-success" />
              )}
              {type === 'error' && (
                <ErrorFill className="m-5xs size-[24px] [&_path]:fill-comp-toast-icon-error" />
              )}
            </div>
          )}
          <div className="flex w-full flex-col gap-2xs">
            {title && (
              <div className="text-body-large text-comp-toast-text">
                {title}
              </div>
            )}
            {description && (
              <div className="text-body-medium text-comp-toast-text">
                {description}
              </div>
            )}
          </div>
          {isShowActionButton && !isTextOverflow && (
            <GhostButton
              hierarchy="tertiary"
              size="small"
              onClick={() => {
                button?.onClick();
                sonnerToast?.dismiss(id);
              }}
              className="min-w-fit"
            >
              {button?.label}
            </GhostButton>
          )}
        </div>
      </div>
      {isShowActionButton && isTextOverflow && (
        <div className="flex justify-end">
          <GhostButton
            hierarchy="tertiary"
            size="small"
            onClick={() => {
              button?.onClick();
              sonnerToast?.dismiss(id);
            }}
          >
            {button?.label}
          </GhostButton>
        </div>
      )}
    </div>
  );
}
